import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TailwindModal from '../ui/TailwindModal'
import type { LevelRewardLine } from '../../common/api'
import {
  STAR_URL,
  isHeroLine,
  levelRewardHeroArt,
  levelRewardHeroKicker,
  levelRewardIcon,
  levelRewardLabel,
  litStarCount,
  rarityGlow,
} from './levelRewardDisplay'
import './LevelRewardModal.css'

interface LevelRewardModalProps {
  open: boolean
  level: number
  /**
   * Pre-claim: generic shapes. Post-claim: the concrete rolls returned by the server.
   * Empty is valid — levels past the reward table (51+) still show the celebration, just
   * with nothing to claim, so this component owns every account level-up.
   */
  rewards: LevelRewardLine[]
  claimed: boolean
  claiming: boolean
  onClaim: () => void
  onClose: () => void
}

const CONFETTI_COLORS = ['#ffd700', '#ff9d2e', '#ffe9a3', '#9d7bff', '#5ec8ff', '#ff6b9d']

/** Full-body art for whichever character / weapon headlines this level's bundle. */
const HeroArt: React.FC<{ line: LevelRewardLine }> = ({ line }) => {
  const { src, fallback } = levelRewardHeroArt(line)
  const [useFallback, setUseFallback] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const resolved = useFallback && fallback ? fallback : src

  return (
    <div className="lrm-hero-art">
      <div className="lrm-hero-halo" style={{ background: `radial-gradient(circle, rgba(255,215,0,0.28) 0%, transparent 68%)` }} />
      <img
        // Remount on source change so the reveal animation replays for the real art.
        key={resolved}
        src={resolved}
        alt=""
        draggable={false}
        className={`lrm-hero-img ${loaded ? 'lrm-hero-img-in' : 'lrm-hero-img-hidden'}`}
        style={{ filter: `drop-shadow(${rarityGlow(line.rarity)})` }}
        onLoad={() => setLoaded(true)}
        onError={() => {
          // Child + worker templates are absent from the `-new` portrait bucket.
          if (!useFallback && fallback && fallback !== src) setUseFallback(true)
          else setLoaded(true)
        }}
      />
    </div>
  )
}

const RarityStars: React.FC<{ rarity?: string }> = ({ rarity }) => {
  const lit = litStarCount(rarity)
  return (
    <div className="lrm-stars-row" aria-hidden>
      {[0, 1, 2].map((i) => (
        <img key={i} src={STAR_URL} alt="" draggable={false} className={`lrm-star ${i < lit ? 'lrm-star-lit' : ''}`} />
      ))}
    </div>
  )
}

/**
 * Account level-up reward celebration.
 *
 * Two beats, deliberately: before the claim the bundle is described but the randomised rolls are
 * unknown (the server has not rolled them yet), so the card shows a sealed medallion. Claiming
 * reveals the actual character / weapon at full size — the same portrait the character details
 * page uses — which is the payoff the reward table is built around.
 */
const LevelRewardModal: React.FC<LevelRewardModalProps> = ({
  open,
  level,
  rewards,
  claimed,
  claiming,
  onClaim,
  onClose,
}) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!open) {
      setIsVisible(false)
      return
    }
    // Next frame so the entrance transition actually plays.
    const id = window.requestAnimationFrame(() => setIsVisible(true))
    return () => window.cancelAnimationFrame(id)
  }, [open])

  // Stable per-mount confetti (positions/delays must not reshuffle on every re-render).
  const confetti = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        left: `${4 + ((i * 53) % 92)}%`,
        delay: `${((i * 137) % 900) / 1000}s`,
        duration: `${2.6 + ((i * 71) % 140) / 100}s`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        spin: i % 2 === 0 ? 'lrm-confetti-spin-a' : 'lrm-confetti-spin-b',
      })),
    [],
  )

  const heroLine = useMemo(() => rewards.find(isHeroLine) ?? null, [rewards])
  const chipLines = useMemo(() => rewards.filter((l) => l !== heroLine), [rewards, heroLine])
  // Only reveal art once the server has told us WHICH character/weapon it rolled.
  const showHeroArt = claimed && heroLine != null && Boolean(heroLine.key || heroLine.image)
  /** Nothing to claim (level past the reward table) — the CTA just dismisses. */
  const nothingToClaim = rewards.length === 0
  const ctaDismisses = claimed || nothingToClaim

  const handleClose = () => {
    setIsVisible(false)
    window.setTimeout(onClose, 260)
  }

  return (
    <TailwindModal
      open={open}
      onClose={handleClose}
      className="lrm-modal"
      zIndex={10060}
      // Never dismissable by a stray backdrop click while a reward is unclaimed.
      closeOnBackdropClick={false}
      disableEscapeKeyDown={!claimed}
      backdropStyle={{ backgroundColor: 'rgba(4, 2, 16, 0.84)' }}
      aria-labelledby="level-reward-title"
    >
      <div className={`lrm-card ${isVisible ? 'lrm-visible' : ''}`}>
        <div className="lrm-card-inner">
          {/*
            All decoration lives inside one clipped layer. The rays rotate and the halos pulse
            with `transform`, and transformed overflow counts toward a scroll container's
            scrollable area — left as direct children of the scrolling card body they made a
            scrollbar flicker in and out in time with the animation.
          */}
          <div className="lrm-fx" aria-hidden>
            <div className="lrm-rays" />
            <div className="lrm-sky">
              {Array.from({ length: 8 }, (_, i) => (
                <span key={i} />
              ))}
            </div>
            <div className="lrm-confetti">
              {confetti.map((c, i) => (
                <span
                  key={i}
                  className={`lrm-confetti-piece ${c.spin}`}
                  style={{
                    left: c.left,
                    background: c.color,
                    animationDelay: c.delay,
                    animationDuration: c.duration,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="lrm-content">
            {/* The level belongs in the headline itself — a separate "Level N" ribbon above it
                just said the same thing twice. */}
            <h2 className="lrm-title" id="level-reward-title">
              {claimed
                ? t('levelUpModal.claimedTitle')
                : t('levelUpModal.levelUpTo', { level })}
            </h2>

            {showHeroArt && heroLine ? (
              <>
                <HeroArt line={heroLine} />
                {heroLine.type !== 'weapon' && <RarityStars rarity={heroLine.rarity} />}
                <p className="lrm-hero-name">{levelRewardLabel(t, heroLine)}</p>
                <p className="lrm-hero-kicker">{levelRewardHeroKicker(t, heroLine)}</p>
              </>
            ) : (
              <div className="lrm-seal" aria-hidden>
                <div className="lrm-seal-halo" />
                <div className="lrm-seal-ring">
                  <span className="lrm-seal-icon">{claimed ? '✓' : '★'}</span>
                </div>
              </div>
            )}

            {chipLines.length > 0 && (
              <>
                <p className="lrm-section-label">{t('levelUpModal.rewardsHeading')}</p>
                <div className="lrm-chips">
                  {chipLines.map((line, index) => (
                    <div
                      className="lrm-chip"
                      key={`${line.type}-${line.key ?? index}`}
                      style={{ animationDelay: `${0.35 + index * 0.07}s` }}
                    >
                      <img className="lrm-chip-icon" src={levelRewardIcon(line)} alt="" draggable={false} />
                      <span className="lrm-chip-label">{levelRewardLabel(t, line)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Pre-claim, a hero reward is teased as a chip instead of art (nothing rolled yet). */}
            {!showHeroArt && heroLine && (
              <div className="lrm-chip lrm-chip-hero" style={{ animationDelay: '0.3s' }}>
                <img className="lrm-chip-icon" src={levelRewardIcon(heroLine)} alt="" draggable={false} />
                <span className="lrm-chip-label">{levelRewardLabel(t, heroLine)}</span>
              </div>
            )}

            <button
              type="button"
              className="lrm-cta"
              onClick={ctaDismisses ? handleClose : onClaim}
              disabled={claiming}
            >
              {claiming
                ? t('common.claiming')
                : ctaDismisses
                  ? t('levelUpModal.continue')
                  : t('common.claim')}
            </button>
          </div>
        </div>
      </div>
    </TailwindModal>
  )
}

export default LevelRewardModal
